import ExcelJS from "exceljs";
import { getJson } from "../../lib/http";
import { brand } from "../../config/theme";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import type { Device } from "../../store/admin/devicesStore";

const COUNT_PROBE_LIMIT = 1;
const TITLE_ROW = 1;
const HEADER_ROW = 3;
const COLUMN_WIDTHS = [24, 32, 32, 26, 32];
const DATE_FORMAT = "dd/mm/yyyy hh:mm:ss";
const DIGITS_ONLY = /^[0-9]{1,15}$/;
const BORDER_COLOR = "FFB6BCC6";
const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export interface BarridoDevice extends Device {
  brand?: { brand?: string; model?: string } | null;
}

export interface BarridoEmptyLabels {
  noVehicle: string;
  unitName: string;
  deviceName: string;
  model: string;
  lastReport: string;
}

export interface BarridoLabels {
  headers: string[];
  empty: BarridoEmptyLabels;
  fileTitle: string;
  sheetName: string;
}

export interface BarridoRow {
  imei: string;
  unit: string;
  device: string;
  model: string;
  lastReport: Date | null;
}

interface BarridoPage {
  data?: BarridoDevice[];
  meta?: { totalItems?: number; totalPages?: number };
}

const fetchDevices = (companyId: string, limit: number, signal?: AbortSignal) =>
  getJson<BarridoPage>(
    "/deviceList",
    { company_id: companyId, page: 1, limit },
    { signal },
    { headers: { "x-company-id": companyId } },
  );

export const fetchDevicesForBarrido = async (
  companyId: string,
  signal?: AbortSignal,
): Promise<BarridoDevice[]> => {
  const probe = await fetchDevices(companyId, COUNT_PROBE_LIMIT, signal);
  const probeDevices = probe.data ?? [];
  const totalItems = probe.meta?.totalItems ?? probeDevices.length;
  if (totalItems <= COUNT_PROBE_LIMIT) return probeDevices;

  const fullCall = await fetchDevices(companyId, totalItems, signal);
  return fullCall.data ?? probeDevices;
};

const lastReportsFromSocket = (): Map<string, number> => {
  const { vehicles } = useVehiclesSocketStore.getState();
  return new Map(
    vehicles
      .filter((vehicle) => (vehicle.lastReport?._seconds ?? 0) > 0)
      .map((vehicle) => [vehicle.id, vehicle.lastReport._seconds]),
  );
};

const excelLocalDate = (seconds: number): Date => {
  const ms = seconds * 1000;
  return new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
};

const unitName = (
  device: BarridoDevice,
  empty: BarridoEmptyLabels,
): string => {
  if (!device.vehicle_id) return empty.noVehicle;
  return device.vehicle?.name?.trim() || empty.unitName;
};

const modelName = (device: BarridoDevice, empty: BarridoEmptyLabels): string =>
  [device.brand?.brand, device.brand?.model]
    .filter(Boolean)
    .join(" ")
    .trim() || empty.model;

export const buildBarridoRows = (
  devices: BarridoDevice[],
  empty: BarridoEmptyLabels,
): BarridoRow[] => {
  const lastReports = lastReportsFromSocket();
  return devices.map((device) => {
    const lastReport = lastReports.get(device.id);
    return {
      imei: device.identifier || device.id,
      unit: unitName(device, empty),
      device: device.name?.trim() || empty.deviceName,
      model: modelName(device, empty),
      lastReport: lastReport ? excelLocalDate(lastReport) : null,
    };
  });
};

export const localDateForFileName = (): string => {
  const now = new Date();
  const day = `${now.getDate()}`.padStart(2, "0");
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  return `${day}-${month}-${now.getFullYear()}`;
};

export const barridoFileTitle = (title: string, companyName: string): string =>
  `${title} - ${companyName.replace(/[\\/:*?"<>|]/g, " ").trim()} - ${localDateForFileName()}`;

const argb = (hex: string): string => `FF${hex.replace("#", "").toUpperCase()}`;

const contrastArgb = (hex: string): string => {
  const value = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((index) =>
    parseInt(value.slice(index, index + 2), 16),
  );
  return r * 0.299 + g * 0.587 + b * 0.114 > 150 ? "FF111827" : "FFFFFFFF";
};

const thinSide = { style: "thin" as const, color: { argb: BORDER_COLOR } };

const CELL_BORDER: Partial<ExcelJS.Borders> = {
  top: thinSide,
  left: thinSide,
  bottom: thinSide,
  right: thinSide,
};

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const writeTextCell = (cell: ExcelJS.Cell, value: string) => {
  cell.alignment = { horizontal: "left", vertical: "middle" };
  if (!DIGITS_ONLY.test(value)) {
    cell.value = value;
    return;
  }
  cell.value = Number(value);
  cell.numFmt = "0".repeat(value.length);
};

const writeSheetHead = (
  ws: ExcelJS.Worksheet,
  labels: BarridoLabels,
  totalCols: number,
) => {
  ws.mergeCells(TITLE_ROW, 1, TITLE_ROW, totalCols);
  const titleCell = ws.getCell(TITLE_ROW, 1);
  titleCell.value = labels.fileTitle;
  titleCell.font = { bold: true, size: 14, color: { argb: argb(brand.accent) } };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(TITLE_ROW).height = 26;

  const headerRow = ws.getRow(HEADER_ROW);
  labels.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: contrastArgb(brand.primary) } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: argb(brand.primary) },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = CELL_BORDER;
    ws.getColumn(index + 1).width = COLUMN_WIDTHS[index] ?? 24;
  });
  headerRow.height = 22;
};

const writeDataRow = (
  ws: ExcelJS.Worksheet,
  row: BarridoRow,
  index: number,
  totalCols: number,
  noLastReport: string,
) => {
  const sheetRow = ws.getRow(HEADER_ROW + 1 + index);

  writeTextCell(sheetRow.getCell(1), row.imei);
  writeTextCell(sheetRow.getCell(2), row.unit);
  writeTextCell(sheetRow.getCell(3), row.device);
  writeTextCell(sheetRow.getCell(4), row.model);

  const dateCell = sheetRow.getCell(5);
  if (row.lastReport) {
    dateCell.value = row.lastReport;
    dateCell.numFmt = DATE_FORMAT;
  } else {
    dateCell.value = noLastReport;
  }
  dateCell.alignment = { horizontal: "center", vertical: "middle" };

  Array.from({ length: totalCols }, (_, column) =>
    sheetRow.getCell(column + 1),
  ).forEach((cell) => {
    cell.border = CELL_BORDER;
  });
};

export const downloadBarrido = async (
  devices: BarridoDevice[],
  labels: BarridoLabels,
): Promise<number> => {
  const rows = buildBarridoRows(devices, labels.empty);
  const totalCols = labels.headers.length;

  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(labels.sheetName, {
    views: [{ state: "frozen", ySplit: HEADER_ROW }],
  });

  writeSheetHead(ws, labels, totalCols);
  rows.forEach((row, index) =>
    writeDataRow(ws, row, index, totalCols, labels.empty.lastReport),
  );

  ws.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW + rows.length, column: totalCols },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(new Blob([buffer], { type: XLSX_MIME }), `${labels.fileTitle}.xlsx`);
  return rows.length;
};
